import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { DateTimePickerButton } from '@/components';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export type PublishType = 'publishNow' | 'draft' | 'scheduled';

interface PublishOptionsSectionBaseProps {
  title: string;
  copy: {
    publishNow: { label: string; description: string };
    draft: { label: string; description: string };
    scheduled: { label: string; description: string };
    scheduledFieldLabel?: string;
    scheduledPlaceholder?: string;
  };
  publishType: PublishType;
  publishTime?: Date | null;
  errorText?: string;
  onPublishTypeChange: (type: PublishType) => void;
  onPublishTimePress?: (anchor?: AnchorRect) => void;
}

export const PublishOptionsSectionBase = memo<PublishOptionsSectionBaseProps>(({
  title,
  copy,
  publishType,
  publishTime,
  errorText,
  onPublishTypeChange,
  onPublishTimePress,
}) => {
  const renderPublishTypeOption = (
    type: PublishType,
    label: string,
    description: string
  ) => (
    <TouchableOpacity
      key={type}
      style={[
        styles.publishOption,
        publishType === type && styles.publishOptionSelected,
      ]}
      onPress={() => onPublishTypeChange(type)}
      activeOpacity={0.85}
    >
      <View style={styles.publishOptionContent}>
        <View style={[
          styles.radioButton,
          publishType === type && styles.radioButtonSelected,
        ]}>
          {publishType === type && (
            <View style={styles.radioButtonInner} />
          )}
        </View>
        <View style={styles.publishOptionText}>
          <Text style={styles.publishOptionLabel}>{label}</Text>
          <Text style={styles.publishOptionDescription}>{description}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>

      {renderPublishTypeOption(
        'publishNow',
        copy.publishNow.label,
        copy.publishNow.description,
      )}

      {renderPublishTypeOption(
        'draft',
        copy.draft.label,
        copy.draft.description,
      )}

      {renderPublishTypeOption(
        'scheduled',
        copy.scheduled.label,
        copy.scheduled.description,
      )}

      {publishType === 'scheduled' && onPublishTimePress && (
        <View style={styles.scheduledTimeContainer}>
          <DateTimePickerButton
            label={copy.scheduledFieldLabel || 'Publish Time *'}
            value={publishTime || new Date()}
            onPress={onPublishTimePress}
            error={errorText}
            placeholder={copy.scheduledPlaceholder || 'Select date and time'}
          />
        </View>
      )}
    </View>
  );
});

PublishOptionsSectionBase.displayName = 'PublishOptionsSectionBase';

const styles = StyleSheet.create({
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
  },
  publishOption: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    padding: 16,
    marginBottom: 12,
  },
  publishOptionSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: `${ADMIN_COLORS.PRIMARY}10`,
  },
  publishOptionContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  radioButtonSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  publishOptionText: {
    flex: 1,
  },
  publishOptionLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  publishOptionDescription: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
  },
  scheduledTimeContainer: {
    marginTop: 16,
    paddingLeft: 32,
  },
});


